import { createTheme } from '@mui/material/styles';
import { green, red } from '@mui/material/colors';

/**
 * デザイントークン（2026-08 改修「上質・建築的」）
 * 色・余白はここを唯一の出典とし、各コンポーネントでのハードコードは禁止。
 */
export const tokens = {
  green: '#437025', // ブランドグリーン（旧 primary.main を維持）
  greenDeep: '#35591e', // hover
  sumi: '#22261f', // テキスト・暗色セクション背景
  sumiDeep: '#181b16', // フッター
  paper: '#faf9f5', // ページ背景
  kinari: '#f2efe7', // 淡色セクション背景
  line: 'rgba(34,38,31,.14)', // 罫線
  muted: '#5c6156', // 補助テキスト
};

/** セクションの縦padding（PC / SP）。mt:30 方式は廃止し、これで区切る。 */
export const sectionPadding = { py: { xs: 10, sm: 15 } }; // 80px / 120px

const rawTheme = createTheme({
  breakpoints: {
    // デザイン確定版のブレークポイント 720px を sm にマッピング
    values: { xs: 0, sm: 720, md: 900, lg: 1200, xl: 1536 },
  },
  palette: {
    primary: {
      light: '#5a8f38',
      main: tokens.green,
      dark: tokens.greenDeep,
      contrastText: '#ffffff',
    },
    // 旧 secondary #70434b（見出し下4pxライン）は廃止。墨色に置換。
    secondary: {
      light: tokens.muted,
      main: tokens.sumi,
      dark: tokens.sumiDeep,
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ffc071',
      dark: '#ffb25e',
    },
    error: {
      light: red[50],
      main: red[500],
      dark: red[700],
    },
    success: {
      light: green[50],
      main: green[500],
      dark: green[700],
    },
    text: {
      primary: tokens.sumi,
      secondary: tokens.muted,
    },
    divider: tokens.line,
  },
  typography: {
    fontFamily: "'Noto Sans JP', sans-serif",
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
});

/** 見出し用（明朝）。uppercase / Roboto Condensed は廃止。 */
const fontHeader = {
  color: tokens.sumi,
  fontFamily: "'Noto Serif JP', serif",
  fontWeight: 500,
};

const down = rawTheme.breakpoints.down('sm');

const theme = {
  ...rawTheme,
  tokens,
  palette: {
    ...rawTheme.palette,
    ...tokens,
    background: {
      ...rawTheme.palette.background,
      default: tokens.paper,
      paper: '#ffffff',
      kinari: tokens.kinari,
      sumi: tokens.sumi,
      sumiDeep: tokens.sumiDeep,
    },
  },
  typography: {
    ...rawTheme.typography,
    fontHeader,
    h1: {
      ...rawTheme.typography.h1,
      ...fontHeader,
      fontSize: 52,
      lineHeight: 1.5,
      letterSpacing: '.1em',
      [down]: { fontSize: 34 },
    },
    h2: {
      ...rawTheme.typography.h2,
      ...fontHeader,
      fontSize: 34,
      lineHeight: 1.4,
      letterSpacing: '.06em',
      [down]: { fontSize: 26 },
    },
    h3: {
      ...rawTheme.typography.h3,
      ...fontHeader,
      fontSize: 22,
      lineHeight: 1.5,
      letterSpacing: '.1em',
      [down]: { fontSize: 20 },
    },
    h4: {
      ...rawTheme.typography.h4,
      ...fontHeader,
      fontSize: 17,
      lineHeight: 1.6,
      letterSpacing: '.12em',
    },
    h5: {
      ...rawTheme.typography.h5,
      ...fontHeader,
      fontSize: 20,
      lineHeight: 1.6,
      letterSpacing: '.1em',
    },
    h6: {
      ...rawTheme.typography.h6,
      ...fontHeader,
      fontSize: 15,
      lineHeight: 1.7,
      letterSpacing: '.16em',
    },
    /** 英字 eyebrow（Our Values / Concept / Works） */
    eyebrow: {
      fontFamily: "'Noto Sans JP', sans-serif",
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: '.28em',
      lineHeight: 1.6,
      color: tokens.green,
      textTransform: 'uppercase',
    },
    subtitle1: {
      ...rawTheme.typography.subtitle1,
      fontSize: 16,
      lineHeight: 1.9,
      letterSpacing: '.08em',
    },
    body1: {
      ...rawTheme.typography.body1,
      fontSize: 16,
      fontWeight: rawTheme.typography.fontWeightRegular,
      lineHeight: 1.9,
      letterSpacing: '.04em',
    },
    body2: {
      ...rawTheme.typography.body2,
      fontSize: 15,
      fontWeight: rawTheme.typography.fontWeightRegular,
      lineHeight: 1.9,
      letterSpacing: '.04em',
    },
  },
};

export default theme;
